import { ArrowUpward, AutoAwesome, CheckCircle, HighlightOff } from "@mui/icons-material";
import { Avatar, Box, ButtonBase, CircularProgress, InputBase, Typography } from "@mui/material";
import { Fragment, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { AssistantAction, AssistantController, AssistantError } from "../../controllers/AssistantController";
import { OwnerConsoleViewController } from "../../controllers/OwnerConsoleViewController";

// The AI Assistant on the report: the homeowner asks about their estimate, records home updates,
// adds or removes a property, saves their mortgage, or asks their agent to review it. Anything that
// changes the report comes back as a card with a Confirm button; nothing changes until it is tapped.
//
// It sits inline in the page rather than floating: agent sites embed the report in an iframe as tall
// as the report, where a fixed panel could open off screen.

const INK = "#1d2433";
const INK_2 = "#4a5263";
const MUTED = "#6b7385";
const LINE = "#e6e9ef";
const BUBBLE = "#f3f5f8";

type CardState = "pending" | "working" | "done" | "failed" | "cancelled";
interface ChatMessage {
	role: "user" | "assistant";
	content: string;
	actions?: Array<AssistantAction & { state: CardState }>;
	// Likely next replies from the assistant, shown as chips while this is the latest message.
	suggestions?: string[];
	// Shown, but not sent back to the assistant (errors).
	local?: boolean;
}
interface Saved {
	conversationId: string;
	messages: ChatMessage[];
}

const STORAGE_PREFIX = "oc_assistant_";

function newConversationId() {
	try {
		return crypto.randomUUID();
	} catch (e) {
		return `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
	}
}

function load(enc?: string): Saved | null {
	if (!enc) return null;
	try {
		const raw = sessionStorage.getItem(STORAGE_PREFIX + enc);
		const saved = raw ? (JSON.parse(raw) as Saved) : null;
		return saved && Array.isArray(saved.messages) && saved.conversationId ? saved : null;
	} catch (e) {
		return null;
	}
}

function save(enc: string | undefined, value: Saved) {
	if (!enc) return;
	try {
		sessionStorage.setItem(STORAGE_PREFIX + enc, JSON.stringify(value));
	} catch (e) {
		// Only keeps the chat across a page reload.
	}
}

// "#16B380" -> "rgba(22, 179, 128, 0.08)"
function withAlpha(hex: string, alpha: number) {
	const match = /^#?([0-9a-f]{6})$/i.exec(hex ?? "");
	if (!match) return `rgba(0, 0, 0, ${alpha})`;
	const n = parseInt(match[1], 16);
	return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

// **bold** and links, inside one line of the assistant's reply.
function inline(text: string, keyBase: string): ReactNode[] {
	const parts: ReactNode[] = [];
	const pattern = /(\*\*[^*]+\*\*|https?:\/\/[^\s)]+)/g;
	let last = 0;
	let match: RegExpExecArray | null;
	let i = 0;
	while ((match = pattern.exec(text))) {
		if (match.index > last) parts.push(text.slice(last, match.index));
		const token = match[0];
		if (token.startsWith("**")) {
			parts.push(<strong key={`${keyBase}-${i++}`}>{token.slice(2, -2)}</strong>);
		} else {
			const url = token.replace(/[.,]+$/, "");
			parts.push(
				<a key={`${keyBase}-${i++}`} href={url} target="_blank" rel="noreferrer" style={{ color: "inherit" }}>
					{url}
				</a>,
			);
			if (url.length < token.length) parts.push(token.slice(url.length));
		}
		last = match.index + token.length;
	}
	if (last < text.length) parts.push(text.slice(last));
	return parts;
}

// Paragraphs and "- " bullets, the only formatting the assistant uses.
function Formatted(props: { text: string }) {
	const blocks = props.text.split(/\n{2,}/);
	return (
		<>
			{blocks.map((block, b) => {
				const lines = block.split("\n").filter((l) => l.trim());
				const bullets = lines.length > 0 && lines.every((l) => /^\s*[-*•]\s+/.test(l));
				if (bullets) {
					return (
						<Box component="ul" key={b} sx={{ m: b ? "8px 0 0" : 0, pl: "20px" }}>
							{lines.map((l, j) => (
								<li key={j} style={{ marginTop: j ? 4 : 0 }}>
									{inline(l.replace(/^\s*[-*•]\s+/, ""), `${b}-${j}`)}
								</li>
							))}
						</Box>
					);
				}
				return (
					<Box component="p" key={b} sx={{ m: b ? "8px 0 0" : 0 }}>
						{lines.map((l, j) => (
							<Fragment key={j}>
								{j > 0 && <br />}
								{inline(l, `${b}-${j}`)}
							</Fragment>
						))}
					</Box>
				);
			})}
		</>
	);
}

export default function AssistantChat() {
	const { appState, appDispatch } = useContext(AppContext);
	const { info, enc, trackView, currentAddrId, colorPallete } = appState;
	const brand = colorPallete?.scheme1 || "#16B380";
	const agentName = info.agent?.agentName || "your agent";
	const firstName = info.client?.name;
	const enabled = info.aiAssistant?.enabled === true;

	const saved = useMemo(() => load(enc), [enc]);
	const [conversationId] = useState(() => saved?.conversationId ?? newConversationId());
	const [messages, setMessages] = useState<ChatMessage[]>(() => saved?.messages ?? []);
	const [draft, setDraft] = useState("");
	const [sending, setSending] = useState(false);
	const listRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => save(enc, { conversationId, messages }), [enc, conversationId, messages]);

	// Keep the newest message in view without scrolling the page (or the agent's site around the frame).
	useEffect(() => {
		const el = listRef.current;
		if (el) el.scrollTop = el.scrollHeight;
	}, [messages, sending]);

	// Before the first message: the usual reasons to open the chat.
	const starters = useMemo(() => {
		const list = ["I think the valuation is wrong", "I've made updates to my home"];
		if (info.mortgageInsights?.enabled) list.push("What's my equity?");
		list.push("Add another property I own");
		list.push(`Talk to ${agentName.split(" ")[0]}`);
		return list;
	}, [agentName, info.mortgageInsights?.enabled]);
	// After that: the assistant's own suggestions for its latest reply.
	const last = messages[messages.length - 1];
	const chips = sending
		? []
		: messages.length === 0
			? starters
			: last?.role === "assistant"
				? (last.suggestions ?? [])
				: [];

	if (!enabled) return null;

	async function refreshReport() {
		try {
			const result = await OwnerConsoleViewController.GetReportConfiguration(enc ?? "");
			if (!result?.report_configuration) return;
			appDispatch({ type: "UPDATE_STATE", key: "report_configuration", value: result.report_configuration });
			if (result.info) appDispatch({ type: "UPDATE_STATE", key: "info", value: result.info });
			const ids: number[] = result.report_configuration.map((r: any) => r.addrId);
			if (ids.length && (currentAddrId === undefined || !ids.includes(currentAddrId))) {
				appDispatch({ type: "SET_CURRENT_ADDR_ID", value: ids[0] });
			}
		} catch (e) {
			console.log(e);
		}
	}

	async function send(text: string) {
		const content = text.trim();
		if (!content || sending) return;
		const next: ChatMessage[] = [...messages, { role: "user", content }];
		setMessages(next);
		setDraft("");
		setSending(true);
		try {
			const history = next
				.filter((m) => !m.local)
				.map((m) => ({ role: m.role, content: m.content }))
				.slice(-24);
			const reply = await AssistantController.SendMessage({
				enc,
				track: trackView,
				conversationId,
				addrId: currentAddrId,
				messages: history,
			});
			setMessages((m) => [
				...m,
				{
					role: "assistant",
					content: reply.reply,
					actions: reply.actions?.map((a) => ({ ...a, state: "pending" as CardState })),
					suggestions: (reply.suggestions ?? []).slice(0, 4),
				},
			]);
		} catch (e) {
			const message =
				e instanceof AssistantError ? e.message : "Sorry, I couldn't connect just now. Please try again.";
			setMessages((m) => [...m, { role: "assistant", content: message, local: true }]);
		} finally {
			setSending(false);
		}
	}

	function setCard(actionId: string, state: CardState) {
		setMessages((list) =>
			list.map((m) =>
				m.actions?.some((a) => a.id === actionId)
					? { ...m, actions: m.actions.map((a) => (a.id === actionId ? { ...a, state } : a)) }
					: m,
			),
		);
	}

	async function decide(action: AssistantAction, decision: "confirm" | "cancel") {
		setCard(action.id, decision === "confirm" ? "working" : "cancelled");
		try {
			const result = await AssistantController.DecideAction({
				enc,
				track: trackView,
				actionId: action.id,
				decision,
			});
			const state: CardState =
				result.status === "done" ? "done" : result.status === "cancelled" ? "cancelled" : "failed";
			setCard(action.id, state);
			// Goes back to the assistant with the next message, so it knows what happened.
			setMessages((m) => [...m, { role: "assistant", content: result.message }]);
			if (result.refresh) await refreshReport();
		} catch (e) {
			setCard(action.id, "pending");
			setMessages((m) => [
				...m,
				{ role: "assistant", content: "Sorry, that didn't go through. Please try again.", local: true },
			]);
		}
	}

	const greeting = `Hi${firstName ? ` ${firstName}` : ""}! If something about your estimate looks off, tell me why and I'll get ${agentName} to review it. I can also add updates you've made to your home, or add another property you own.`;

	return (
		<Box
			component="section"
			aria-label="Ask about your home value"
			sx={{
				backgroundColor: "#fff",
				border: `1px solid ${LINE}`,
				borderRadius: "16px",
				overflow: "hidden",
				textAlign: "left",
				color: INK,
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: "12px", p: "16px 18px 12px" }}>
				<Avatar
					src={info.agent?.agent_photo || undefined}
					alt=""
					sx={{ width: 36, height: 36, bgcolor: withAlpha(brand, 0.12), color: brand }}
				>
					<AutoAwesome sx={{ fontSize: 20 }} />
				</Avatar>
				<Box sx={{ minWidth: 0 }}>
					<Typography sx={{ fontWeight: 700, fontSize: "16px", lineHeight: 1.3, color: INK }}>
						Questions about your estimate?
					</Typography>
					<Typography sx={{ fontSize: "12px", color: MUTED, lineHeight: 1.4 }}>
						AI assistant for {agentName}. {agentName} can see this chat.
					</Typography>
				</Box>
			</Box>

			<Box
				ref={listRef}
				role="log"
				aria-live="polite"
				sx={{
					px: "18px",
					pb: "8px",
					maxHeight: messages.length ? 440 : "none",
					overflowY: "auto",
					display: "flex",
					flexDirection: "column",
					gap: "10px",
				}}
			>
				<Bubble role="assistant" brand={brand}>
					{greeting}
				</Bubble>
				{messages.map((m, i) => (
					<Fragment key={i}>
						<Bubble role={m.role} brand={brand}>
							{m.role === "assistant" ? <Formatted text={m.content} /> : m.content}
						</Bubble>
						{m.actions?.map((a) => (
							<ActionCard key={a.id} action={a} brand={brand} onDecide={decide} />
						))}
					</Fragment>
				))}
				{sending && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: "8px",
							color: MUTED,
							fontSize: "13px",
							py: "4px",
						}}
					>
						<CircularProgress size={14} sx={{ color: brand }} /> Thinking...
					</Box>
				)}
			</Box>

			{chips.length > 0 && (
				<Box
					role="group"
					aria-label="Suggested replies"
					sx={{
						display: "flex",
						flexWrap: "wrap",
						gap: "8px",
						px: "18px",
						pb: "12px",
						pt: messages.length ? "4px" : 0,
					}}
				>
					{chips.map((s) => (
						<ButtonBase
							key={s}
							onClick={() => send(s)}
							sx={{
								px: "12px",
								py: "7px",
								borderRadius: "999px",
								border: `1px solid ${withAlpha(brand, 0.35)}`,
								color: brand,
								backgroundColor: withAlpha(brand, 0.05),
								fontSize: "13px",
								fontWeight: 600,
								textAlign: "left",
								"&:hover": { backgroundColor: withAlpha(brand, 0.1) },
							}}
						>
							{s}
						</ButtonBase>
					))}
				</Box>
			)}

			<Box
				component="form"
				onSubmit={(e) => {
					e.preventDefault();
					send(draft);
				}}
				sx={{
					display: "flex",
					alignItems: "flex-end",
					gap: "8px",
					p: "10px 12px 12px 18px",
					borderTop: `1px solid ${LINE}`,
				}}
			>
				<InputBase
					value={draft}
					onChange={(e) => setDraft(e.target.value.slice(0, 1500))}
					onKeyDown={(e) => {
						if (e.key === "Enter" && !e.shiftKey) {
							e.preventDefault();
							send(draft);
						}
					}}
					placeholder="Ask about your home value..."
					multiline
					maxRows={4}
					inputProps={{ "aria-label": "Your question" }}
					// 16px keeps phones from zooming in on focus.
					sx={{ flex: 1, fontSize: "16px", color: INK, py: "6px" }}
				/>
				<ButtonBase
					type="submit"
					aria-label="Send"
					disabled={sending || !draft.trim()}
					sx={{
						width: 38,
						height: 38,
						borderRadius: "50%",
						flex: "none",
						color: "#fff",
						backgroundColor: brand,
						"&.Mui-disabled": { backgroundColor: "#e6e9ef", color: "#9aa1ae" },
					}}
				>
					<ArrowUpward sx={{ fontSize: 20 }} />
				</ButtonBase>
			</Box>
		</Box>
	);
}

function Bubble(props: { role: "user" | "assistant"; brand: string; children: ReactNode }) {
	const mine = props.role === "user";
	return (
		<Box
			sx={{
				alignSelf: mine ? "flex-end" : "flex-start",
				maxWidth: "88%",
				px: "14px",
				py: "10px",
				borderRadius: mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
				backgroundColor: mine ? props.brand : BUBBLE,
				color: mine ? "#fff" : INK,
				fontSize: "14px",
				lineHeight: 1.5,
				whiteSpace: mine ? "pre-wrap" : "normal",
				wordBreak: "break-word",
			}}
		>
			{props.children}
		</Box>
	);
}

function ActionCard(props: {
	action: AssistantAction & { state: CardState };
	brand: string;
	onDecide: (a: AssistantAction, d: "confirm" | "cancel") => void;
}) {
	const { action, brand, onDecide } = props;
	const settled = action.state === "done" || action.state === "failed" || action.state === "cancelled";
	return (
		<Box
			sx={{
				alignSelf: "stretch",
				border: `1.5px solid ${settled ? LINE : withAlpha(brand, 0.45)}`,
				backgroundColor: settled ? "#fafbfc" : withAlpha(brand, 0.04),
				borderRadius: "14px",
				p: "14px",
			}}
		>
			<Typography sx={{ fontWeight: 700, fontSize: "14px", color: INK, lineHeight: 1.4 }}>
				{action.title}
			</Typography>
			{action.detail && (
				<Typography sx={{ fontSize: "13px", color: INK_2, mt: "4px", lineHeight: 1.5 }}>
					{action.detail}
				</Typography>
			)}
			{action.state === "pending" && (
				<Box sx={{ display: "flex", gap: "8px", mt: "12px" }}>
					<ButtonBase
						onClick={() => onDecide(action, "confirm")}
						sx={{
							px: "16px",
							height: 38,
							borderRadius: "10px",
							fontSize: "14px",
							fontWeight: 700,
							color: "#fff",
							backgroundColor: brand,
							"&:hover": { filter: "brightness(.94)" },
						}}
					>
						{action.confirmLabel || "Confirm"}
					</ButtonBase>
					<ButtonBase
						onClick={() => onDecide(action, "cancel")}
						sx={{
							px: "14px",
							height: 38,
							borderRadius: "10px",
							fontSize: "14px",
							fontWeight: 600,
							color: INK_2,
							"&:hover": { backgroundColor: "#f1f3f7" },
						}}
					>
						Not now
					</ButtonBase>
				</Box>
			)}
			{action.state === "working" && (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
						mt: "12px",
						fontSize: "13px",
						color: MUTED,
					}}
				>
					<CircularProgress size={14} sx={{ color: brand }} />
					{action.kind === "add_property"
						? "Looking up its value. This can take up to a minute..."
						: "Working on it..."}
				</Box>
			)}
			{settled && (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: "6px",
						mt: "10px",
						fontSize: "13px",
						color: MUTED,
					}}
				>
					{action.state === "done" ? (
						<CheckCircle sx={{ fontSize: 16, color: brand }} />
					) : (
						<HighlightOff sx={{ fontSize: 16 }} />
					)}
					{action.state === "done"
						? "Done"
						: action.state === "cancelled"
							? "Not changed"
							: "Didn't go through"}
				</Box>
			)}
		</Box>
	);
}
